<template>
  <VdPage
    :class="$style.configurationSimulatorPage"
    title="Configuration simulator"
    with-background
    show-back-arrow
    @back="onBack"
  >
    <VdForm :form="form" save-button-label="Simulate" class="form" @submit="onSimulate">
      <VdFormField prop="platform" label="Platform" required>
        <VdChoice
          v-model="form.platform"
          type="radio"
          button-style
          @update:model-value="(platform: string) => updateUrlQuery('platform', platform)"
        >
          <VdChoiceItem value="ios">iOS</VdChoiceItem>
          <VdChoiceItem value="android">Android</VdChoiceItem>
        </VdChoice>
      </VdFormField>

      <VdFormField prop="gameVersion" label="Game version">
        <VdInputVersion
          v-model="form.gameVersion"
          placeholder="latest"
          @update:model-value="(version: string) => updateUrlQuery('version', version)"
        />
      </VdFormField>

      <VdFormField prop="segmentIds" label="Select segment(s)" :class="$style.segmentField">
        <SegmentsSelectInput
          v-model="form.segmentIds"
          multiple
          :game-id="gameId"
          :version-id="versionId"
          @update:model-value="(values) => updateUrlQuery('segmentIds', values)"
        />
      </VdFormField>

      <VdFormField prop="simulationDate">
        <Checkbox v-model="form.useSimulationDate" :class="$style.simulationDateChecbox" binary />
        <label for="form.useSimulationDate" :class="$style.simulationDateLabel">Simulate specific date/time</label>
        <DatePicker
          id="durationStart"
          v-model="form.simulationDate"
          :class="$style.simulationDateField"
          date-format="dd/mm/yy"
          placeholder="Simulation date"
          :disabled="!form.useSimulationDate"
          show-time
          @date-select="(value) => updateUrlQuery('simulationDate', value.toISOString())"
        />
      </VdFormField>

      <VdFormField prop="abTestCohortIds" label="Select A/B Test(s) and cohort(s)" class="abtests-field">
        <CohortSelector
          :model-value="form.abTestCohortIds"
          :game-id="gameId"
          :segment-ids="form.segmentIds"
          :platform="form.platform"
          :game-version="form.gameVersion"
          clearable
          :version-id="versionId"
          @update:model-value="(values) => updateUrlQuery('abTestCohortIds', values)"
        />
      </VdFormField>

      <template #actions="{ onSubmit }">
        <div :class="$style.simulatorFormAction">
          <vd-button v-if="form.platform" @click="onReset">Reset</vd-button>
          <vd-button :loading="loading || isLoadingSimulation" type="primary" @click="onSubmit">Simulate</vd-button>
        </div>
      </template>
    </VdForm>
    <ConfigurationViewerTabs
      v-if="showConfiguration"
      :loading="isLoadingSimulation"
      :simulation="simulationResult"
      :simulation-url="simulationUrl"
      :raw-simulation="rawClassSimulationResult"
      :offer-simulation="offerSimulationResult"
      :offer-simulation-url="offerSimulationUrl"
      :raw-offer-simulation="rawOfferSimulationResult"
      :shop-simulation="shopSimulationResult"
      :shop-simulation-url="shopSimulationUrl"
      :raw-shop-simulation="rawShopSimulationResult"
    />
  </VdPage>
</template>

<script setup lang="ts">
import { VdChoice, VdChoiceItem, VdForm, VdFormField, VdInputVersion, VdPage } from "@voodoo.io/voodoo-ui";
import Checkbox from "primevue/checkbox";
import DatePicker from "primevue/datepicker";
import { PropType, computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";

import CohortSelector from "@/components/CohortSelector/CohortSelector.vue";
import SegmentsSelectInput from "@/components/SegmentsSelectInput/SegmentsSelectInput.vue";
import { useGameDisplayPageQuery } from "@/graphql";
import useNotifications from "@/hooks/notifications";
import links from "@/router/links";

import useLiveopsApiClient from "@/client/liveopsAPI";
import useSettingsApiClient from "@/client/settingsAPI";
import { SimulationClass, SimulationOffer, SimulationShop, SimulationUrlQueryParams } from "@/modules/Simulation/types";
import {
  RawOfferSimulationPayload,
  RawSimulationPayload,
  SimulationParameters,
  buildOfferSimulationResult,
  buildParameters,
  buildShopSimulationResult,
  buildSimulationResult,
} from "@/modules/Simulation/utils";
import ConfigurationViewerTabs from "./components/ConfigurationViewerTabs.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },

  useSimulationDate: {
    type: Boolean,
    default: false,
  },

  simulationDate: {
    type: Date,
    required: false,
  },

  // eslint-disable-next-line vue/require-default-prop
  query: {
    type: Object as PropType<SimulationUrlQueryParams>,
    // the default values are set in the router see @/router/configuration.js
  },
});

const router = useRouter();
const { notifyError } = useNotifications();

const { client: settingsClient } = useSettingsApiClient();
const { client: liveopsClient } = useLiveopsApiClient();

const queryOptions = ref({
  keepPreviousResult: true,
  returnPartialData: true,
});

const { loading, result } = useGameDisplayPageQuery(
  () => ({
    gameId: props.gameId,
    versionId: props.versionId,
  }),
  queryOptions
);

const form = reactive({
  platform: props.query?.platform || "ios",
  gameVersion: props.query?.gameVersion,
  segmentIds: props.query?.segmentIds,
  abTestCohortIds: props.query?.abTestCohortIds,
  useSimulationDate: !!props.query?.simulationDate,
  simulationDate: new Date(props.query?.simulationDate),
});

watch(
  () => props.query,
  (newQuery) => {
    form.platform = newQuery?.platform || "ios";
    form.gameVersion = newQuery?.gameVersion;
    form.segmentIds = newQuery?.segmentIds;
    form.abTestCohortIds = newQuery?.abTestCohortIds;
    form.simulationDate = newQuery?.simulationDate ? new Date(newQuery.simulationDate) : null;
  },
  { deep: true, immediate: true }
);

/**
 *  build the url query params from the form values
 */
const buildUrlQuery = (currentState: {
  platform?: string;
  gameVersion?: string;
  segmentIds?: string[];
  abTestCohortIds?: string[];
  simulationDate?: Date;
}) => {
  let query = {};
  if (currentState.platform) {
    query = { ...query, platform: currentState.platform };
  }

  if (currentState.gameVersion) {
    query = { ...query, gameVersion: currentState.gameVersion || "1000.0.0" };
  }

  if (currentState.segmentIds?.length) {
    query = { ...query, segmentIds: currentState.segmentIds.join(",") };
  }

  if (currentState.abTestCohortIds?.length) {
    query = { ...query, abTestCohortIds: currentState.abTestCohortIds.join(",") };
  }

  if (currentState.simulationDate) {
    query = { ...query, simulationDate: currentState.simulationDate };
  }

  return query;
};
const updateUrlQuery = (field: string, value: string | string[]) => {
  const state = {
    ...form,
    [field]: value,
  };

  if (typeof state.simulationDate !== "string") {
    state.simulationDate = state.simulationDate?.toISOString();
  }
  router.push({
    name: links.games.simulator,
    params: { gameId: props.gameId, versionId: props.versionId },
    query: buildUrlQuery(state),
  });
};

const showConfiguration = ref(false);
const rawClassSimulationResult = ref("");
const simulationUrl = ref("");
const rawOfferSimulationResult = ref("");
const offerSimulationUrl = ref("");
const rawShopSimulationResult = ref("");
const shopSimulationUrl = ref("");
const isLoadingSimulation = ref(false);
const versionUUID = computed(() => result.value?.game?.version?.id);
const simulationResult = ref<SimulationClass[]>([]);
const offerSimulationResult = ref<SimulationOffer[]>([]);
const shopSimulationResult = ref<SimulationShop>({});

const updateClassSimulationResults = (newResult: RawSimulationPayload) => {
  simulationResult.value = buildSimulationResult(newResult);
};

const updateOfferSimulationResults = (newResult: RawOfferSimulationPayload) => {
  offerSimulationResult.value = buildOfferSimulationResult(newResult);
};

const updateShopSimulationResults = (newResult: SimulationShop) => {
  shopSimulationResult.value = buildShopSimulationResult(newResult);
};

const onReset = () => {
  form.platform = "";
  form.gameVersion = "";
  form.segmentIds = [];
  form.abTestCohortIds = [];
  form.useSimulationDate = false;
  showConfiguration.value = false;
};

const onBack = () => {
  router.push({
    name: links.games.configurations.list,
    params: { gameId: props.gameId, versionId: props.versionId },
  });
};

const loadOfferAndShopSimulation = async (simulationParameters: SimulationParameters, shopId: string) => {
  const RetrieveOffersSimulationPath = "/offers/simulations";
  const RetrieveShopsSimulationPath = "/shop/simulations";
  const params = buildParameters(simulationParameters, props.gameId, versionUUID.value);
  const shopParams = {
    appId: props.gameId,
    versionId: versionUUID.value,
    shopId,
  };

  try {
    const offersResponse = await liveopsClient.get(RetrieveOffersSimulationPath, { params });
    updateOfferSimulationResults(offersResponse.data);
    offerSimulationUrl.value = `${offersResponse.config.baseURL!}${offersResponse.config.url!}?${new URLSearchParams(
      offersResponse.config.params
    ).toString()}`;
    rawOfferSimulationResult.value = JSON.stringify(offersResponse.data, null, 2);
  } catch {
    notifyError("An error occurred while simulating the configuration. Please try again later.");
  }
  try {
    const shopResponse = await liveopsClient.get(RetrieveShopsSimulationPath, { params: shopParams });
    updateShopSimulationResults(shopResponse.data);
    shopSimulationUrl.value = `${shopResponse.config.baseURL!}${shopResponse.config.url!}?${new URLSearchParams(
      shopResponse.config.params
    ).toString()}`;
    rawShopSimulationResult.value = JSON.stringify(shopResponse.data, null, 2);
  } catch {
    notifyError("An error occurred while simulating the configuration. Please try again later.");
  }
};

const retrieveShopIdFromConfig = (data: RawSimulationPayload) => {
  if (data["VT.ShopManagementClass"]) {
    return data["VT.ShopManagementClass"][0].shopId;
  }
  return null;
};

const runSimulation = async (simulationParameters: SimulationParameters) => {
  const RetrieveSettingsSimulationPath = "/settings/simulations";
  const params = buildParameters(simulationParameters, props.gameId, versionUUID.value);

  try {
    const response = await settingsClient.get(RetrieveSettingsSimulationPath, { params });
    updateClassSimulationResults(response.data);
    simulationUrl.value = `${response.config.baseURL!}${response.config.url!}?${new URLSearchParams(
      response.config.params
    ).toString()}`;
    rawClassSimulationResult.value = JSON.stringify(response.data, null, 2);

    const configuredShopId = retrieveShopIdFromConfig(response.data);

    if (configuredShopId) {
      await loadOfferAndShopSimulation(simulationParameters, configuredShopId);
    }
    showConfiguration.value = true;
  } catch {
    notifyError("An error occurred while simulating the configuration. Please try again later.");
    showConfiguration.value = false;
  }
};

const onSimulate = async () => {
  showConfiguration.value = false;
  isLoadingSimulation.value = true;
  await runSimulation(form);
  isLoadingSimulation.value = false;
};
</script>

<style module lang="scss">
.configurationSimulatorPage {
  :global .vd-page__content .el-form .vd-form__content {
    display: grid;
    column-gap: size("XL");
    max-width: 1000px;
  }

  :global .el-form-item__content .el-select,
  :global .el-form-item__content .el-cascader {
    width: 100%;
  }
}

.simulatorFormAction {
  display: flex;
  justify-content: flex-end;

  :global .vd-button.el-button {
    margin-left: size("M");
  }
}

.simulationDateCheckbox {
  margin-left: 5px;
}

.simulationDateLabel {
  margin: 0 5px;
}
.simulationDateField {
  margin-left: 5px;
}
</style>
